import template from "./degrees-decimal-minutes.html";
import "./degrees-decimal-minutes.less";

import {
	ddmToDegrees,
	getDecomposedDdm
} from "services/geo-service.ts";
import { roundToRoundingPoint } from "services/math-service.js";
import decimalDegreeMinuteField from "../../../field-sub-components/decimal-degree-minute-field/decimal-degree-minute-field.js";
import {
	createCoordinateAxisSource,
	getComparableCoordinatePart,
	hasCoordinatePart,
	resolveCoordinateAxisValue
} from "../coordinate-axis-state.ts";
import { isValueNullish } from "../../../../../utils/valueUtilities";

export class Controller {
	currentValue = {
		latitude: {},
		longitude: {}
	};

	constructor($scope) {
		this._scope = $scope;
	}

	$onInit() {
		this.setValue(this.value);
		this._initialized = true;

		this._scope.$watch(
			() => this.currentValue,
			() => {
				this.setCompassDirection();
				this.publishCurrentValue();
			},
			true
		);
	}

	$onChanges(changes) {
		if(this._initialized && !isValueNullish(changes.value) && changes.value.currentValue !== changes.value.previousValue) {
			this.setValue(changes.value.currentValue);
		}
	}

	setValue(value) {
		this.currentValue = {
			latitude: hasCoordinatePart(value?.Latitude) ? getDecomposedDdm(value.Latitude) : {},
			longitude: hasCoordinatePart(value?.Longitude) ? getDecomposedDdm(value.Longitude) : { isNegative: true }
		};
		this.currentValue.latitude.isLatitude = true;
		this.currentValue.longitude.isLatitude = false;
		this.sourceAxes = {
			latitude: createCoordinateAxisSource(
				value?.Latitude,
				this.currentValue.latitude,
				this.getAxisState
			),
			longitude: createCoordinateAxisSource(
				value?.Longitude,
				this.currentValue.longitude,
				this.getAxisState
			)
		};
		this.lastObservedState = this.getEditableState();
		this.setCompassDirection();
	}

	getAxisState = axis => {
		return JSON.stringify({
			degrees: getComparableCoordinatePart(axis.degrees),
			minutes: getComparableCoordinatePart(axis.minutes),
			isNegative: axis.isNegative === true
		});
	}

	getEditableState() {
		return JSON.stringify({
			latitude: this.getAxisState(this.currentValue.latitude),
			longitude: this.getAxisState(this.currentValue.longitude)
		});
	}

	handleFieldChanged = () => {
		this.setCompassDirection();
		this.publishCurrentValue();
	}

	publishCurrentValue() {
		const editableState = this.getEditableState();
		if(editableState === this.lastObservedState) {
			return;
		}

		this.lastObservedState = editableState;
		const coordinateHasInput = this.hasCoordinateInput();
		const value = coordinateHasInput ? {
			FieldId: this.field.Id,
			Latitude: this.getAxisValue(
				this.currentValue.latitude,
				this.sourceAxes.latitude,
				90
			),
			Longitude: this.getAxisValue(
				this.currentValue.longitude,
				this.sourceAxes.longitude,
				180
			)
		} : undefined;

		this.onValueChange({
			value,
			hasInput: coordinateHasInput,
			triggerEvent: true
		});
	}

	getAxisValue(axis, source, maximumDegrees) {
		return resolveCoordinateAxisValue(axis, source, this.getAxisState, editableAxis => {
			if(!hasCoordinatePart(editableAxis.degrees) || !hasCoordinatePart(editableAxis.minutes)) {
				return undefined;
			}

			const value = ddmToDegrees({
				degrees: editableAxis.degrees,
				minutes: {
					raw: editableAxis.minutes,
					rounded: editableAxis.minutes
				},
				isNegative: editableAxis.isNegative
			});
			if(value === undefined || Math.abs(value) > maximumDegrees) {
				return undefined;
			}

			return roundToRoundingPoint(value, this.field);
		});
	}

	hasCoordinateInput() {
		return hasCoordinatePart(this.currentValue.latitude.degrees)
			|| hasCoordinatePart(this.currentValue.latitude.minutes)
			|| hasCoordinatePart(this.currentValue.longitude.degrees)
			|| hasCoordinatePart(this.currentValue.longitude.minutes);
	}

	setCompassDirection() {
		this.latCompassLetter = this.currentValue.latitude.isNegative ? "S" : "N";
		this.lonCompassLetter = this.currentValue.longitude.isNegative ? "W" : "E";
	}
}

export default angular
	.module("vesl.components.report-form.coordinate.degrees-decimal-minutes", [decimalDegreeMinuteField])
	.component("degreesDecimalMinutes", {
		template,
		controller: Controller,
		bindings: {
			field: "<",
			value: "<",
			onValueChange: "&"
		}
	}).name;
